<script setup lang="ts">
import {useAuth} from "@/scripts/store/auth";
import {computed, nextTick, ref} from "vue";

const authStore = useAuth()

const email = ref('')
const password = ref('')
const showPasswordInput = ref(false)
const isNewAccountMode = ref(false)
const loading = ref(false)
const passwordInput = ref()

const passwordLabel = computed(() => {
    if (isNewAccountMode.value) {
        return 'Придумайте пароль'
    } else {
        return 'Введите пароль'
    }
})

const isSubmitButtonActive = computed(() => {
    const re = /([a-zA-Z0-9._-]+@[a-zA-Z0-9._-]+\.[a-zA-Z0-9._-]+)/;
    return re.test(email.value);
})

const resetEmail = async () => {
    showPasswordInput.value = false
    password.value = ''
}

const submitLogin = async () => {
    if (!isSubmitButtonActive) {
        return
    }

    loading.value = true;

    if (password.value.length === 0) {
        const isEmailExist = await authStore.isEmailExist(email.value)

        isNewAccountMode.value = !isEmailExist
        showPasswordInput.value = true
        await nextTick(() => {
            passwordInput.value.focus();
        })
    } else {
        if (isNewAccountMode.value) {
            const result = await authStore.createUser(
                email.value,
                password.value,
                Intl.DateTimeFormat().resolvedOptions().timeZone
            )

            if (result) {
                const params = new URLSearchParams(window.location.search)
                const tariff = params.get('tariff')

                if (tariff) {
                    window.location = '/billing' + window.location.search
                } else {
                    window.location = '/qrcodes'
                }
            }
        } else {
            const result = await authStore.login(
                email.value,
                password.value
            )

            if (result) {
                const params = new URLSearchParams(window.location.search)
                const tariff = params.get('tariff')

                if (tariff) {
                    window.location = '/billing' + window.location.search
                } else {
                    window.location = '/dashboard' + window.location.search
                }
            }
        }
    }

    loading.value = false;

}
</script>

<template>
    <div class="min-h-full flex flex-col justify-center py-12 sm:px-6 lg:px-8">
        <div class="sm:mx-auto sm:w-full sm:max-w-md">
            <a href="./">
                <img class="mx-auto h-12 w-auto" src="/public/misc/landing/logo-black.svg"
                     alt="Workflow">
            </a>
            <h2 class="mt-6 text-center text-3xl font-extrabold text-gray-900">
                Войти или зарегистрироваться
            </h2>
        </div>

        <div class="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
            <div class="py-8 px-4 sm:px-10">
                <div class="space-y-6">
                    <div>
                        <label for="email" class="block text-lg text-gray-700">
                            Email адрес
                        </label>
                        <div class="mt-1">
                            <input id="email" name="email" type="email" autocomplete="email"
                                   v-on:keyup.enter="submitLogin"
                                   v-model="email"
                                   @input="resetEmail"
                                   autofocus
                                   class="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm">
                        </div>
                    </div>

                    <div v-if="showPasswordInput">
                        <label for="password" class="block text-lg text-gray-700">
                            {{ passwordLabel }}
                        </label>
                        <div class="mt-1">
                            <input id="password" name="password" type="password" autocomplete="current-password"
                                   v-model="password"
                                   ref="passwordInput"
                                   v-on:keyup.enter="submitLogin"
                                   class="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm">
                        </div>
                    </div>

                    <div class="flex items-center justify-between" v-if="showPasswordInput && !isNewAccountMode">
                        <div class="text-sm">
                            <a href="/password/reset" class="font-medium text-indigo-600 hover:text-indigo-500">
                                Забыли пароль?
                            </a>
                        </div>
                    </div>

                    <div v-if="loading">
                        <loader></loader>
                    </div>
                    <div v-else>
                        <button type="submit"
                                :disabled="!isSubmitButtonActive"
                                @click="submitLogin"
                                class="disabled:bg-indigo-200 w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500">
                            Начать
                        </button>
                    </div>

                </div>
            </div>
        </div>

        <div class="text-gray-300 sm:mx-auto sm:w-full sm:max-w-md">
            <div class="px-4 sm:px-10">
                Нажимая кнопку «Начать», вы соглашаетесь с <a href="/terms">Лицензионным соглашением</a> и <a
                href="/policy">Политикой обработки персональных данных</a>.
            </div>
        </div>
    </div>
</template>
